import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { PageHeader } from "@ui/components/common/page-header";
import { useI18n } from "@core/i18n/i18n";
import { physioMeTabletBreakpoint, physioMeMobileBreakpoint } from "@ui/style";
import LearnHeroDesktop from "@assets/icons/learn-herodesktop.svg";
import LearnHeroTablet from "@assets/icons/learn-herotablet.svg";
import LearnHeroMobile from "@assets/icons/learn-heromobile.svg";
import EmptyLearn from "@assets/icons/empty-learn.svg";
import ArticlesIcon from "@assets/icons/articles.svg";
import VideosIcon from "@assets/icons/videos.svg";
import { learnManager } from "@core/service-container";
import { useNavigate } from "react-router-dom";
import { LearnItem, MediaType } from "@domain/learn/learn-service";
import { LearnItemCard } from "@ui/components/learn/learn-item-card";
import { colors } from "@ui/style/colors";
import { fonts } from "@ui/style/font";
import { getTabBackground, getTabColor, TabVariant } from "@utils/tab-utils";

export const LearnPage = (): JSX.Element => {
	const { format } = useI18n();
	const navigate = useNavigate();

	const [items, setItems] = useState<LearnItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedTabs, setSelectedTabs] = useState<("ALL" | MediaType)[]>(["ALL"]);

	const handleNavigate = useCallback(
		(documentId: string) => {
			navigate(`/learn/${documentId}`);
		},
		[navigate],
	);

	const handleTabClick = useCallback((tabKey: "ALL" | MediaType) => {
		setSelectedTabs(prev => {
			if (tabKey === "ALL") {
				return ["ALL"];
			}
			const isAlreadySelected = prev.includes(tabKey);
			if (isAlreadySelected) {
				const newTabs = prev.filter(t => t !== tabKey);
				return newTabs.length === 0 ? ["ALL"] : newTabs;
			}
			const newTabs = prev.filter(t => t !== "ALL");
			return [...newTabs, tabKey];
		});
	}, []);

	const fetchItems = useCallback(() => {
		let mounted = true;
		setLoading(true);
		setError(null);

		let mediaTypeFilter: MediaType | undefined;
		if (!selectedTabs.includes("ALL") && selectedTabs.length === 1) {
			mediaTypeFilter = selectedTabs[0] as MediaType;
		}

		void learnManager
			.getItems(0, 20, mediaTypeFilter)
			.then(resp => {
				if (!mounted) return;
				setItems(resp.items);
			})
			.catch((e: unknown) => {
				console.warn("[LearnPage] failed to load items", e);
				if (mounted) {
					setError("Failed to load content. Please try again.");
				}
			})
			.finally(() => {
				if (mounted) {
					setLoading(false);
				}
			});
		return () => {
			mounted = false;
		};
	}, [selectedTabs]);

	useEffect(() => fetchItems(), [fetchItems]);

	const renderContent = (): JSX.Element => {
		if (loading) {
			return (
				<LoadingContainer role="status" aria-live="polite">
					<LoadingText>Loading content...</LoadingText>
				</LoadingContainer>
			);
		}

		if (error) {
			return (
				<ErrorContainer role="alert" aria-live="assertive">
					<ErrorTitle>Unable to Load Content</ErrorTitle>
					<ErrorText>{error}</ErrorText>
					<RetryButton onClick={fetchItems}>Try Again</RetryButton>
				</ErrorContainer>
			);
		}

		if (items.length === 0) {
			return (
				<>
					<EmptyGraphicWrapper aria-hidden="true">
						<EmptyLearn />
					</EmptyGraphicWrapper>
					<EmptyTitle>{format("learn.empty.title")}</EmptyTitle>
					<EmptySubtitle>{format("learn.empty.subtitle")}</EmptySubtitle>
				</>
			);
		}

		return (
			<ItemsGrid role="list" aria-label="Learning content items">
				{items.map(item => (
					<LearnItemCard key={item.documentId} item={item} onNavigate={handleNavigate} />
				))}
			</ItemsGrid>
		);
	};

	return (
		<div>
			<HeroSection>
				<HeroSvgWrapper aria-hidden>
					<HeroSvgDesktop aria-hidden>
						<LearnHeroDesktop preserveAspectRatio="xMidYMid slice" focusable={false} />
					</HeroSvgDesktop>
					<HeroSvgTablet aria-hidden>
						<LearnHeroTablet preserveAspectRatio="xMidYMid slice" focusable={false} />
					</HeroSvgTablet>
					<HeroSvgMobile aria-hidden>
						<LearnHeroMobile preserveAspectRatio="xMidYMid slice" focusable={false} />
					</HeroSvgMobile>
				</HeroSvgWrapper>
				<HeaderContainer>
					<PageHeader title={format("tabbar.learn")} />
				</HeaderContainer>
				<HeroContent>
					<HeroTitle>{format("learn.hero.title")}</HeroTitle>
					<HeroSubtitle>{format("learn.hero.subtitle")}</HeroSubtitle>
				</HeroContent>
			</HeroSection>
			<EmptySection as="div">
				<EmptyContent>
					<TabsRow>
						{(
							[
								{ key: "ALL", label: format("learn.tab.all"), Icon: undefined, variant: "all" as TabVariant },
								{
									key: MediaType.ARTICLE,
									label: format("learn.tab.articles"),
									Icon: ArticlesIcon,
									variant: "default" as TabVariant,
								},
								{
									key: MediaType.VIDEO,
									label: format("learn.tab.videos"),
									Icon: VideosIcon,
									variant: "default" as TabVariant,
								},
							] as {
								key: "ALL" | MediaType;
								label: string;
								Icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
								variant?: TabVariant;
							}[]
						).map(tab => (
							<TabButton
								key={tab.key}
								variant={tab.variant}
								active={selectedTabs.includes(tab.key)}
								onClick={() => handleTabClick(tab.key)}
							>
								{tab.Icon ? (
									<TabIcon aria-hidden active={selectedTabs.includes(tab.key)}>
										<tab.Icon />
									</TabIcon>
								) : null}
								{tab.label}
							</TabButton>
						))}
					</TabsRow>
					{renderContent()}
				</EmptyContent>
			</EmptySection>
		</div>
	);
};

const HeroSection = styled.div`
	width: 100%;
	position: relative;
	overflow: hidden;
	display: block;
	padding: 0;

	/* Mobile: smaller height */
	@media ${physioMeMobileBreakpoint} {
		min-height: var(--learn-hero-height, 250px);
	}

	/* Tablet: medium height */
	@media (min-width: 501px) and (max-width: 1199px) {
		min-height: var(--learn-hero-height, 380px);
	}

	/* Desktop: default height */
	@media (min-width: 1200px) {
		min-height: var(--learn-hero-height, 300px);
	}
`;

const HeroContent = styled.div`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	z-index: 2;
	padding: 0 16px;
	width: 100%;
	max-width: 100%;
	box-sizing: border-box;

	/* Mobile: responsive padding and max-width */
	@media ${physioMeMobileBreakpoint} {
		padding: 0 20px;
		max-width: 100%;
	}

	/* Tablet: max-width for better readability */
	@media (min-width: 501px) and (max-width: 1199px) {
		max-width: 720px;
		padding: 0 24px;
	}

	/* Desktop: larger max-width */
	@media (min-width: 1200px) {
		max-width: 1392px;
		padding: 0 24px;
	}
`;

const HeroSvgWrapper = styled.div`
	position: absolute;
	inset: 0;
	z-index: 0;
	pointer-events: none;
	display: block;
	svg {
		width: 100%;
		height: 100%;
		display: block;
		transform-origin: center;
		transform: translateY(0%);
	}

	@media ${physioMeTabletBreakpoint} {
		svg {
			/* shift the SVG upward on tablet to mimic background-position: center top */
			transform: translateY(-12%);
		}
	}
`;

const HeroSvgDesktop = styled.div`
	display: none;
	width: 100%;
	height: 100%;
	@media (min-width: 1025px) {
		display: block;
	}
`;

const HeroSvgTablet = styled.div`
	display: none;
	width: 100%;
	height: 100%;
	@media (min-width: 521px) and (max-width: 1024px) {
		display: block;
	}
`;

const HeroSvgMobile = styled.div`
	display: none;
	width: 100%;
	height: 100%;
	@media (max-width: 520px) {
		display: block;
	}
`;

const HeroTitle = styled.h1`
	margin: 0;
	font-family: ${fonts.body};
	font-size: 24px;
	line-height: 1.2;
	color: ${colors.black};
	font-weight: 300;
	letter-spacing: 0.2px;
	max-width: 100%;
	word-break: break-word;
	width: 100%;

	/* Mobile: smaller font size */
	@media ${physioMeMobileBreakpoint} {
		font-size: 20px;
		line-height: 1.3;
	}

	/* Tablet: medium font size */
	@media (min-width: 501px) and (max-width: 1199px) {
		font-size: 28px;
		max-width: 720px;
	}

	/* Desktop: larger font size */
	@media (min-width: 1200px) {
		font-size: 30px;
		max-width: 900px;
	}
`;

const HeroSubtitle = styled.p`
	margin: 12px 0 0 0;
	font-family: ${fonts.body};
	font-size: 24px;
	line-height: 1.4;
	color: ${colors.black};
	font-weight: 300;
	max-width: 100%;
	letter-spacing: 0.2px;
	width: 100%;
	word-break: break-word;

	/* Mobile: smaller font size and adjusted spacing */
	@media ${physioMeMobileBreakpoint} {
		font-size: 18px;
		line-height: 1.5;
		margin-top: 8px;
	}

	/* Tablet: medium font size */
	@media (min-width: 501px) and (max-width: 1199px) {
		font-size: 26px;
		max-width: 720px;
	}

	/* Desktop: larger font size */
	@media (min-width: 1200px) {
		font-size: 30px;
		max-width: 1100px;
	}
`;

const HeaderContainer = styled.div`
	position: absolute;
	top: 24px;
	left: 24px;
	z-index: 3;
	@media ${physioMeTabletBreakpoint} {
		top: 12px;
		left: 12px;
	}
`;

const EmptySection = styled.section`
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 0px 88px 88px 88px;
	box-sizing: border-box;
	background: transparent;
	@media ${physioMeTabletBreakpoint} {
		padding: 32px 12px;
	}
`;

const EmptyContent = styled.div`
	max-width: 960px;
	text-align: center;
`;

const EmptyGraphicWrapper = styled.div`
	display: block;
	margin: 0 auto 20px auto;
	width: 220px;
	max-width: 60%;
	svg {
		width: 100%;
		height: auto;
		display: block;
	}
	@media ${physioMeTabletBreakpoint} {
		width: 160px;
		margin-bottom: 16px;
	}
`;

const EmptyTitle = styled.h2`
	margin: 0 0 8px 0;
	font-family: ${fonts.header};
	font-size: 24px;
	line-height: 1;
	font-weight: 700;
	color: ${colors.black};
`;

const EmptySubtitle = styled.p`
	margin: 0;
	font-family: ${fonts.body};
	font-size: 16px;
	line-height: 1.5;
	color: ${colors.black};
	font-weight: 400;
	font-style: normal;
`;

const LoadingContainer = styled.div`
	width: 100%;
	text-align: center;
	padding: 80px 20px;
`;

const LoadingText = styled.p`
	font-family: ${fonts.body};
	font-size: 16px;
	color: ${colors.grey50};
	margin: 0;
`;

const ErrorContainer = styled.div`
	width: 100%;
	max-width: 500px;
	margin: 40px auto;
	padding: 24px;
	background: ${colors.rejection10};
	border: 1px solid ${colors.rejection25};
	border-radius: 12px;
	text-align: center;
`;

const ErrorTitle = styled.h2`
	font-family: ${fonts.header};
	font-size: 20px;
	font-weight: 700;
	color: ${colors.rejectionDark};
	margin: 0 0 8px 0;
`;

const ErrorText = styled.p`
	font-family: ${fonts.body};
	font-size: 14px;
	color: ${colors.rejectionDark};
	margin: 0 0 16px 0;
	line-height: 1.5;
`;

const RetryButton = styled.button`
	padding: 10px 24px;
	background: ${colors.primary};
	color: ${colors.white};
	border: none;
	border-radius: 8px;
	font-family: ${fonts.body};
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.2s ease;

	&:hover {
		background: ${colors.primaryDark};
	}

	&:focus {
		outline: 2px solid ${colors.primary};
		outline-offset: 2px;
	}
`;

const TabsRow = styled.div`
	display: flex;
	gap: 30px;
	margin: 40px 0 40px 0;
	flex-wrap: wrap;
	justify-content: center;
`;

const TabIcon = styled.span<{ active?: boolean }>`
	display: inline-flex;
	width: 16px;
	height: 16px;
	margin-right: 8px;
	align-items: center;
	justify-content: center;
	color: ${props => (props.active ? colors.white : colors.black)};
	transition: all 0.2s ease;
	svg {
		width: 100%;
		height: 100%;
		display: block;
		${props => (props.active ? "filter: brightness(0) invert(1);" : "filter: brightness(1) invert(0);")}
	}
`;

const TabButton = styled.button<{ active?: boolean; variant?: TabVariant }>`
	border: 1px solid ${props => (props.active ? colors.primary75 : colors.primary25)};
	background: ${props => getTabBackground(props.active)};
	color: ${props => (props.active ? colors.white : getTabColor(props.active, props.variant))};
	padding: 8px 14px;
	border-radius: 20px;
	font-family: ${fonts.body};
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		border-color: ${colors.primary};
	}

	&:focus {
		outline: 2px solid ${colors.primary};
		outline-offset: 2px;
	}
`;

const ItemsGrid = styled.div`
	display: grid;
	/* Desktop: 4 fixed-width cards (270px) centered */
	grid-template-columns: repeat(4, 270px);
	gap: 48px 40px;
	margin-top: 8px;
	justify-content: center;
	@media ${physioMeTabletBreakpoint} {
		/* Tablet: 2 columns */
		grid-template-columns: repeat(2, 270px);
	}
	@media (max-width: 520px) {
		grid-template-columns: 1fr;
		justify-content: stretch;
	}
`;
